import clsx from "clsx";

import { useDesignElementManager } from "@hooks/use-design-element-manager";

import {
  LEGACY_PLATFORMS,
  type LegacyPlatform,
} from "../../utms/legacy-platforms";
import { Platform } from "../constants";

import { SetupSection } from "./components/setup-section";
import { Code, OrderedList, SubSubSectionHeader } from "./components/styled";
import {
  ConstructAndCopyUrl,
  URLVerification,
  UtmParamsClipboard,
} from "./components/utms";
import { VideoContent } from "./components/video-content";
import { type PlatformDetails } from "./platform-details";

const PLATFORM = Platform.TIKTOK;

export const TikTokDetails: PlatformDetails = {
  northbeamDocUrl:
    "https://docs.northbeam.io/docs/setting-up-tiktok-ads-for-northbeam",
  officialDocUrl: "",
  additionalDetails: [
    {
      title: "What does the integration do?",
      /* eslint-disable max-len */
      content:
        "Northbeam natively integrates with the TikTok Ads API. By doing so, we pull all your current and historical Campaigns, Adsets, and Ads from your account, along with a list of useful metrics. Notably, we pull impressions, which fuel our view model and unlock our Clicks + Modeled Views attribution model. We also pull ad spend and list of in-platform conversion & creative metrics to help you with decision making.",
    },
    {
      title: "What about TikTok shops?",
      content:
        "For Northbeam to attribute TikTok shops orders, they must be present and tagged in your orders table.",
    },
  ],
};

export const TikTokSetup = ({
  connectionContent,
  legacyPlatforms,
}: {
  connectionContent: React.ReactNode;
  legacyPlatforms?: LegacyPlatform[];
}) => {
  const { designElementManager } = useDesignElementManager();

  const includeLegacyUTMs = legacyPlatforms?.includes("triple-whale");
  return (
    <>
      <SetupSection title="Log into TikTok Ads to connect integration">
        {connectionContent}
      </SetupSection>
      <SetupSection title="Follow the UTM instructions in the video below">
        <div className="space-y-4">
          <VideoContent
            title="Learn how to structure TikTok UTMs"
            platform={PLATFORM}
            videoUrl="https://www.loom.com/embed/d7b028f553c64c63a6683bbd0e276f3e"
          />
          <div className="space-y-2">
            <SubSubSectionHeader>
              Make sure you’ve followed these steps:
            </SubSubSectionHeader>
            <div className="flex gap-2">
              <span>2a.</span>
              <span>You will need to add UTM parameters to all</span>
              <Code>destination URLs</Code>
              <span>for new and existing ads</span>
            </div>
          </div>
        </div>
      </SetupSection>
      <SetupSection title="Copy these UTM parameters into TikTok Ads">
        <UtmParamsClipboard
          platform={PLATFORM}
          className={clsx("max-w-2xl", includeLegacyUTMs && "pb-10")}
          aggregateMultipleParams={false}
        />
        {includeLegacyUTMs && (
          <div className="flex flex-col gap-4 text-sm">
            <h1 className="mx-0 mt-0 mb-0 text-lg font-semibold leading-normal heading">
              OR use legacy UTMs for historic ads
            </h1>
            <span>
              <b>Note:</b> Legacy UTMs prevent historic ads from resetting to
              “learning phase”. For the better attribution, we prefer that you
              use Northbeam’s UTM.
            </span>
            <span>
              <b>Note:</b> If you have multiple legacy attribution tools for
              this platform, you can only use one legacy UTM for each historic
              ad.
            </span>
            <div className="flex flex-col gap-4 rounded-lg border">
              <div className="flex gap-2 items-center p-4 border-b">
                <div className="inline-flex">
                  {designElementManager
                    .getDesignElement("triple-whale")
                    .getComponent()}
                  <div className="-ml-2">
                    {designElementManager
                      .getDesignElement("tiktok")
                      .getComponent()}
                  </div>
                </div>
                <h1 className="mx-0 mt-0 mb-0 text-lg font-semibold leading-normal heading">
                  {LEGACY_PLATFORMS.getLabel("triple-whale")}, Legacy TikTok Ads
                  UTMs
                </h1>
              </div>
              <div className="flex flex-col gap-4 px-4 pb-6 text-lg leading-normal">
                <span className="font-semibold">
                  Add following parameters + values:
                </span>
                <OrderedList variant="disc" className="ml-2">
                  <>
                    <span>tw_source = </span>
                    <Code>tiktok&tw_adid=_CID_</Code>
                  </>
                </OrderedList>
              </div>
            </div>
          </div>
        )}
      </SetupSection>
      <SetupSection
        title="Paste the destination URL - and copy the new URL with Northbeam’s URL
          parameters"
      >
        <ConstructAndCopyUrl platform={PLATFORM} doNotEncodeUtmParams />
      </SetupSection>
      <URLVerification platform={PLATFORM} />
    </>
  );
};
